import { Link } from 'react-router-dom';
import { HamburgerMenu } from '../components/HamburgerMenu';
import { NewsletterSignup } from '../components/NewsletterSignup';
import { motion } from 'framer-motion';
import { ArrowRight, Coffee, Croissant, Users } from 'lucide-react';
import { SEO } from '../components/SEO';
const quickLinks: {
  label: string;
  to: string;
}[] = [{
  label: 'Menu',
  to: '/menu'
}, {
  label: 'Catering',
  to: '/catering'
}, {
  label: 'Events',
  to: '/events'
}, {
  label: 'Location',
  to: '/location'
}];
export function AboutUs() {
  return <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-[#D97E3A] selection:text-white" data-id="element-177" data-mp="38t2sliffntxq">
      <SEO title="About Grounded - Community Coffee Shop in Livermore" description="Grounded is a family-owned, multi-roaster coffee shop in Livermore, CA. We curate beans from California's best independent roasters and serve Coffee and Local Delights, while giving back to our community." canonical="https://www.groundedlivermore.com/about" data-id="element-178" data-mp="2uttagd7o2pr4" />
      <HamburgerMenu data-id="element-179" data-mp="2ftmi1wuq0t3u" />

      <main data-id="element-180" data-mp="2ft30wicwa8qv">
        {/* Hero */}
        <section className="relative h-[70vh] w-full overflow-hidden flex items-center justify-center" data-id="element-181" data-mp="2aw2s3zonurw2">
          <div className="absolute inset-0 z-0" data-id="element-182" data-mp="2omzirkz10z0h">
            <img src="https://images.unsplash.com/photo-1442512595367-42736f3e576a?q=80&w=2670&auto=format&fit=crop" alt="Coffee shop interior" className="w-full h-full object-cover opacity-30 grayscale contrast-125" data-id="element-183" data-mp="2ompohl6y85oz" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-[#0a0a0a]" data-id="element-184" data-mp="1ry0ai5yfzx8a" />
            <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/concrete-wall.png')] mix-blend-overlay" data-id="element-185" data-mp="25xsznwubm5e5" />
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="relative z-10 text-center px-4" data-id="element-186" data-mp="3hbmyl110aw6n">
            <span className="text-[#D97E3A] font-inter font-medium tracking-widest text-sm uppercase mb-3 block" data-id="element-187" data-mp="2a075m0n0l4mf">
              Since 2023
            </span>
            <span className="text-white/50 font-inter font-medium tracking-[0.3em] text-xs uppercase mb-5 block" data-id="element-188" data-mp="2f0bm3qnx9zcn">
              Our Story
            </span>
            <h1 className="font-bebas text-white leading-[0.85] tracking-tight text-[13vw] lg:text-[8.5rem]" data-id="element-189" data-mp="2nxvpp699c7qf">
              GROUNDED IN LIVERMORE
            </h1>
          </motion.div>
        </section>

        {/* Intro */}
        <section className="py-24 px-6 md:px-12 lg:px-24" data-id="element-190" data-mp="1v8eygbxdiwvs">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl mx-auto" data-id="element-191" data-mp="208jgi6dxjzo4">
            <p className="font-inter text-xl text-white/75 leading-relaxed border-l-2 border-[#D97E3A] pl-6" data-id="element-192" data-mp="17j2rlipir0wn">
              Grounded is a family-owned coffee shop in Livermore. Established
              in 2023, Grounded was created to give the community a place to
              gather and be neighbors. Influenced by cafes we've loved
              throughout our travels, Grounded is our dedication to our town -
              Livermore, CA.
            </p>
            <p className="font-inter text-base text-white/50 leading-relaxed mt-6 pl-6" data-id="element-193" data-mp="qzy3jao90p2t">
              You can read the whole story in{' '}
              <Link to="/journal/our-story" className="text-[#D97E3A] underline underline-offset-4 decoration-[#D97E3A]/40 hover:decoration-[#D97E3A] transition-colors" data-id="element-194" data-mp="m3g32mzybcwa">
                the Journal
              </Link>
              .
            </p>
          </motion.div>
        </section>

        {/* Our approach */}
        <section aria-labelledby="approach-heading" className="pb-24 px-6 md:px-12 lg:px-24" data-id="element-195" data-mp="3alvwf8kkp2m">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24" data-id="element-196" data-mp="3ujj62fg4cdea">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-197" data-mp="1ygehr8ob2d8h">
              <h2 id="approach-heading" className="font-bebas text-4xl md:text-5xl mb-6 text-[#D97E3A]" data-id="element-198" data-mp="1tnldelwr16wx">
                OUR APPROACH
              </h2>
              <p className="font-inter text-lg text-white/70 leading-relaxed mb-6" data-id="element-199" data-mp="1jpdzyd1o56zo">
                Most shops put their name on a single house roast. We do it
                differently. Grounded curates artisanal beans from some of the
                best independent roasters in our area. On a given day you might
                find Proyecto Diaz on drip, Temple pulling as our espresso,
                Chromatic steeping into cold brew, and a rotating guest on the
                pour-over bar. It's a running tour of California's best, which
                means there's always something new in your cup.
              </p>
              <p className="font-inter text-lg text-white/70 leading-relaxed" data-id="element-200" data-mp="3a8nasl3lbet1">
                And it isn't only coffee. We're "Coffee and Local Delights" - we
                bring that same curator's eye to the case and the shelf: Butter
                &amp; Batter cookies, Bubb's bagels, Ginger Lab ginger beer,
                Living Flora kefir, JTM seasoning. Grab something here and
                you're tasting a neighbor's craft.
              </p>
            </motion.div>

            {/* Feature tiles */}
            <div className="space-y-8" data-id="element-201" data-mp="arxe5cg92mrj">
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="bg-[#111] p-8 border border-white/5 relative overflow-hidden" data-id="element-202" data-mp="6qvl5dcxf6y0">
                <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-203" data-mp="bie0vv678yay">
                  <Coffee size={100} aria-hidden="true" data-id="element-204" data-mp="ggfhm9g5klj9" />
                </div>
                <h3 className="font-bebas text-3xl mb-2 relative" data-id="element-205" data-mp="giihrmb0rv1p">
                  MULTI-ROASTER, BY DESIGN
                </h3>
                <p className="font-inter text-white/60 relative" data-id="element-206" data-mp="qfegj0b492t2">
                  We champion California's small independent roasters instead of
                  roasting our own - so the bar stays exciting and the cup keeps
                  changing.
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="bg-[#111] p-8 border border-white/5 relative overflow-hidden" data-id="element-207" data-mp="145qcjaatnlph">
                <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-208" data-mp="1i6ate0x6f7gc">
                  <Croissant size={100} aria-hidden="true" data-id="element-209" data-mp="32p4tb6wl5fuh" />
                </div>
                <h3 className="font-bebas text-3xl mb-2 relative" data-id="element-210" data-mp="2ylyrcbgiaatt">
                  COFFEE + LOCAL DELIGHTS
                </h3>
                <p className="font-inter text-white/60 relative" data-id="element-211" data-mp="2to6rmv9mdkf2">
                  Made-to-order food and a case full of small-batch local
                  makers, right alongside the coffee.
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="bg-[#111] p-8 border border-white/5 relative overflow-hidden group hover:border-[#D97E3A]/30 transition-colors" data-id="element-212" data-mp="3hczocty2fwaq">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-213" data-mp="8yzh825qfj5d">
                  <Users size={100} aria-hidden="true" data-id="element-214" data-mp="38o94wmeknrx5" />
                </div>
                <h3 className="font-bebas text-3xl mb-2 relative" data-id="element-215" data-mp="38m6alq6dzcmm">
                  <Link to="/community" className="hover:text-[#D97E3A] transition-colors" data-id="element-216" data-mp="3dfr8li7zc6lt">
                    GROUNDED IN THE COMMUNITY
                  </Link>
                </h3>
                <p className="font-inter text-white/60 relative mb-4" data-id="element-217" data-mp="3reqxmqzemnii">
                  Name your school when you order and 10% goes back to it. We
                  fundraise for local causes and open our space for gatherings.
                </p>
                <Link to="/community" className="relative inline-flex items-center gap-2 font-bebas text-lg tracking-wide text-[#D97E3A]" data-id="element-218" data-mp="3idam2fwusfcp">
                  LEARN MORE
                  <ArrowRight size={16} aria-hidden="true" className="group-hover:translate-x-1 transition-transform" data-id="element-219" data-mp="26xlxjk3yb89s" />
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Closing */}
        <section aria-labelledby="visit-heading" className="py-24 bg-[#111] border-y border-white/5 px-6 md:px-12 lg:px-24" data-id="element-220" data-mp="1zhqqx080tksp">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl mx-auto text-center" data-id="element-221" data-mp="28hdvfuw1r6pz">
            <h2 id="visit-heading" className="font-bebas text-5xl md:text-7xl mb-6" data-id="element-222" data-mp="1ug1fvkkst74m">
              COME SIT <span className="text-[#D97E3A]" data-id="element-223" data-mp="1pojnhkwucev9">WITH US</span>
            </h2>
            <p className="font-inter text-lg text-white/70 leading-relaxed" data-id="element-224" data-mp="11zgw3lcen7nk">
              We're made for breakfast, brunch, and lunch - good for groups,
              families, a laptop morning, or catching up with a friend. The
              space is open for community events, and we cater. Find us at 53
              Wright Brothers Ave, Ste A, Livermore.
            </p>

            <nav aria-label="Explore Grounded" className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3" data-id="element-225" data-mp="1im9ckrsj448p">
              {quickLinks.map(link => <Link key={link.to} to={link.to} className="font-bebas text-xl tracking-wide text-[#D97E3A] hover:text-white transition-colors" data-id="element-226" data-mp="19lkzq0evg9xe">
                  {link.label}
                </Link>)}
            </nav>
          </motion.div>
        </section>

        <NewsletterSignup data-id="element-227" data-mp="uxq8kogiidmf" />

        {/* Footer */}
        <footer className="border-t border-white/10 py-12 text-center bg-[#0a0a0a]" data-id="element-228" data-mp="pxkkfi9gb993">
          <p className="font-bebas text-2xl text-white/50" data-id="element-229" data-mp="pzopi3j36eay">
            © {new Date().getFullYear()} GROUNDED LIVERMORE
          </p>
        </footer>
      </main>
    </div>;
}